import { useState, useEffect } from "react";
import { axiosInstance } from "@/App";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import TaskCard from "@/components/TaskCard";
import TaskFormModal from "@/components/TaskFormModal";
import { toast } from "sonner";

const TaskBoard = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const statuses = [
    { id: "To Do", label: "To Do", testId: "column-todo" },
    { id: "In Progress", label: "In Progress", testId: "column-in-progress" },
    { id: "Done", label: "Done", testId: "column-done" },
  ];

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await axiosInstance.get("/tasks");
      setTasks(response.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = () => {
    setSelectedTask(null);
    setIsModalOpen(true);
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axiosInstance.delete(`/tasks/${taskId}`);
      setTasks(tasks.filter((t) => t.id !== taskId));
      toast.success("Task deleted successfully");
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const response = await axiosInstance.put(`/tasks/${taskId}`, {
        status: newStatus,
      });
      setTasks(tasks.map((t) => (t.id === taskId ? response.data : t)));
      toast.success("Task status updated");
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task status");
    }
  };

  const handleTaskSaved = (savedTask) => {
    if (selectedTask) {
      setTasks(tasks.map((t) => (t.id === savedTask.id ? savedTask : t)));
    } else {
      setTasks([...tasks, savedTask]);
    }
    setIsModalOpen(false);
    setSelectedTask(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="board-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="task-board-page">
      <div className="p-8 pb-6 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2" data-testid="board-title">
              Board
            </h1>
            <p className="text-gray-600 dark:text-gray-400" data-testid="board-subtitle">
              Organize tasks in Kanban view
            </p>
          </div>
          <Button
            onClick={handleCreateTask}
            data-testid="create-task-button"
            className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Task
          </Button>
        </div>
      </div>

      <div className="flex-1 p-8 overflow-x-auto bg-[#fafafa] dark:bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="kanban-board">
            {statuses.map((status) => {
              const statusTasks = tasks.filter((task) => task.status === status.id);
              
              return (
                <div
                  key={status.id}
                  data-testid={status.testId}
                  className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 min-h-[600px] border border-gray-200 dark:border-gray-800"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white" data-testid={`${status.testId}-title`}>
                      {status.label}
                    </h2>
                    <span
                      className="bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium px-2.5 py-0.5 rounded-full"
                      data-testid={`${status.testId}-count`}
                    >
                      {statusTasks.length}
                    </span>
                  </div>

                  <div className="space-y-3" data-testid={`${status.testId}-tasks`}>
                    {statusTasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onEdit={handleEditTask}
                        onDelete={handleDeleteTask}
                        onStatusChange={handleStatusChange}
                      />
                    ))}
                    
                    {statusTasks.length === 0 && (
                      <div
                        className="text-center py-12 text-gray-400 dark:text-gray-600"
                        data-testid={`${status.testId}-empty`}
                      >
                        No tasks
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <TaskFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTask(null);
        }}
        task={selectedTask}
        onSave={handleTaskSaved}
      />
    </div>
  );
};

export default TaskBoard;
